import { ArrowUpDown, FileUp, Plus, Tags } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useCountryScope, usePermissions } from '@/features/auth';
import { countryName, useSelectedCountry } from '@/features/country-scope';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { DataTableViewOptions } from '@/shared/components/data-table/data-table-view-options';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { PageContainer } from '@/shared/components/page-container';
import { ExportActions } from '@/shared/components/export-actions';
import { PageHeader } from '@/shared/components/page-header';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

import { exportBrands } from '../api/brands.api';
import { useBrands, useSetBrandStatus } from '../api/brands.queries';
import { BrandEditDialog, BrandFormDialog } from '../components/brand-form-dialog';
import { BrandOrderDialog } from '../components/brand-order-dialog';
import { BrandsFilters } from '../components/brands-filters';
import { createBrandColumns } from '../components/brands-table-columns';
import { CatalogueChangeDialog } from '../components/catalogue-change-dialog';
import { DeleteBrandDialog } from '../components/delete-catalogue-dialogs';
import type { BrandListItem } from '../types/brand.types';

/**
 * Brands list — the reference implementation for configuration modules.
 *
 * Read this before building a new CRUD screen. It demonstrates, in order:
 * URL-driven table state, a server-shaped query, a permission-gated primary
 * action, composed toolbar/table/pagination, distinct loading, error and two
 * kinds of empty state, a create/edit dialog, and a confirmation dialog for a
 * consequential action that previews its impact on customers (§87).
 *
 * Visibility and sellability are summarised for the selected Admin country;
 * the Admin API re-checks that the country is in the staff member's scope.
 *
 * Note what this page does NOT do: it does not fetch, transform or sort data
 * itself. It declares what it wants and renders the result.
 */
export function BrandsPage() {
  const tableState = useTableSearchParams({ defaultSort: 'name', defaultDirection: 'asc' });
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const { countries, country } = useSelectedCountry();

  // Dialog state is local UI state — it is not shareable, so it does not
  // belong in the URL, and it is not global, so it does not belong in a store.
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<BrandListItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BrandListItem | null>(null);
  const [isOrderOpen, setOrderOpen] = useState(false);

  const statusFilter = tableState.getFilter('status');
  const categoryFilter = tableState.getFilter('categoryId');

  // The input is controlled straight from the URL, so typing is instant; only
  // the value that reaches the query is debounced. That keeps one owner for
  // the search term and still issues one request per pause, not per keystroke.
  const debouncedSearch = useDebouncedValue(tableState.search, 300);

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: debouncedSearch || undefined,
      isActive: statusFilter === null ? undefined : statusFilter === 'active',
      categoryId: categoryFilter ? Number(categoryFilter) : undefined,
      countryId: country?.id,
      sort: tableState.sort ?? undefined,
      direction: tableState.direction,
    }),
    [
      tableState.page,
      tableState.pageSize,
      debouncedSearch,
      tableState.sort,
      tableState.direction,
      statusFilter,
      categoryFilter,
      country?.id,
    ],
  );

  const brands = useBrands(params);
  const setStatus = useSetBrandStatus();

  const countryLabel = country ? countryName(country) : null;
  const columns = useMemo(
    () =>
      createBrandColumns({
        countryLabel,
        onEdit: (brand) => setEditingBrandId(brand.id),
        onToggleActive: setPendingToggle,
        onDelete: setPendingDelete,
      }),
    [countryLabel],
  );

  const table = useDataTable({
    data: brands.data,
    columns,
    tableState,
    getRowId: (brand) => String(brand.id),
  });

  // A brand exists in every country, so creating one needs all-country scope.
  const canCreate = can('catalogue.manage') && allCountries;
  // The import sheet also writes provider costs.
  const canImport = canCreate && can('catalogue.cost.read');

  return (
    <PageContainer>
      <PageHeader
        title="Brands"
        description="Merchant brands in the Digital Codex catalogue. Each brand holds the denominations that are actually sold."
        actions={
          <div className="flex flex-wrap gap-2">
            <ExportActions
              excel={() => {
                const { page: _page, pageSize: _pageSize, ...filters } = params;
                return exportBrands(filters);
              }}
            />
            {canImport && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/brands/import">
                  <FileUp className="size-4" aria-hidden />
                  Import from Excel
                </Link>
              </Button>
            )}
            {canCreate && (
              <Button variant="outline" size="sm" onClick={() => setOrderOpen(true)}>
                <ArrowUpDown className="size-4" aria-hidden />
                Storefront brand order
              </Button>
            )}
            {canCreate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" aria-hidden />
                Add brand
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
          actions={<DataTableViewOptions table={table} />}
        >
          <BrandsFilters tableState={tableState} />
        </DataTableToolbar>

        <DataTable
          table={table}
          isLoading={brands.isLoading}
          isFetching={brands.isFetching}
          error={brands.error}
          onRetry={() => void brands.refetch()}
          emptyState={
            // Two genuinely different empty states. Telling someone "no brands
            // found" when they have simply over-filtered sends them looking
            // for a data problem that does not exist (§63).
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={Tags}
                title="No brands match your filters"
                description="Try a different search term, or clear the filters to see the whole catalogue."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Tags}
                title="No brands yet"
                description={
                  canCreate
                    ? 'Add the first merchant brand to start building the catalogue.'
                    : 'No brands have been added to the catalogue yet.'
                }
                action={
                  canCreate ? (
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      <Plus className="size-4" aria-hidden />
                      Add brand
                    </Button>
                  ) : undefined
                }
              />
            )
          }
        />

        <DataTablePagination table={table} />
      </div>

      <BrandFormDialog open={isCreateOpen} onOpenChange={setCreateOpen} />

      {isOrderOpen && <BrandOrderDialog onClose={() => setOrderOpen(false)} />}

      <BrandEditDialog brandId={editingBrandId} onClose={() => setEditingBrandId(null)} />

      {pendingToggle && (
        <CatalogueChangeDialog
          key={pendingToggle.id}
          brandId={pendingToggle.id}
          request={{ change: pendingToggle.isActive ? 'deactivateBrand' : 'activateBrand' }}
          onOpenChange={(open) => !open && setPendingToggle(null)}
          title={pendingToggle.isActive ? 'Deactivate this brand?' : 'Activate this brand?'}
          description={
            pendingToggle.isActive ? (
              <>
                <strong>{pendingToggle.name}</strong> will be hidden from the storefront in every
                country and its denominations will stop being sold. Existing orders are unaffected,
                and you can reactivate it at any time.
              </>
            ) : (
              <>
                <strong>{pendingToggle.name}</strong> will become visible in every country where its
                denominations are offered.
              </>
            )
          }
          confirmLabel={pendingToggle.isActive ? 'Deactivate' : 'Activate'}
          variant={pendingToggle.isActive ? 'destructive' : 'default'}
          isPending={setStatus.isPending}
          countryName={(id) => {
            const match = countries.find((candidate) => candidate.id === id);
            return match ? countryName(match) : `#${id}`;
          }}
          onConfirm={(reason) =>
            setStatus.mutate(
              { brandId: pendingToggle.id, isActive: !pendingToggle.isActive, reason },
              { onSuccess: () => setPendingToggle(null) },
            )
          }
        />
      )}

      {pendingDelete && (
        <DeleteBrandDialog
          key={pendingDelete.id}
          brand={pendingDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </PageContainer>
  );
}
